import { computed, inject, Injectable, Injector, runInInjectionContext, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchAndLogError } from '@rma/generic/rxjs/operators/catch-and-log-error';
import { PublicUserContextService } from '@rma/public/accounts/user';
import { LocationDetails } from '@rma/public/locations/public/api-location-details';
import { finalize, forkJoin, map, Observable, of, share, Subject, switchMap, tap } from 'rxjs';
import { ShortlistResult } from '../domain/shortlist';
import { ApiShortlistStorageService } from './api-shortlist-storage.service';
import { LocalShortlistStorageService } from './local-shortlist-storage.service';
import { createShortlistAgents, ShortlistAgents } from './shortlist-agents';
import { ShortlistMergePrompt } from './shortlist-merge-prompt';

export interface CompareAgent {
  shortlistCount: Signal<number>;
  shortlist: Signal<string[]>;
  // The shortlist as last loaded from storage — moved only by a load or an auth flip, never by a
  // local add/remove. A consumer that already applies a mutation to its own state optimistically
  // can track this instead of `shortlist` so that mutation doesn't echo back as a redundant fetch.
  // Only safe for the mutations the consumer does patch itself: track `shortlist` for the rest, or
  // it will miss them. Read `shortlist` for the live codes — this one lags by any local mutation.
  loadedShortlist: Signal<string[]>;
  location: LocationDetails | undefined;
  // True while a shortlist built before sign-in is waiting on the user to say how it joins the
  // account's own list. Nothing is merged until `settlePendingMerge` runs.
  hasPendingMerge: Signal<boolean>;
  remove(agentCode: string): Observable<ShortlistResult>;
  clear(): Observable<ShortlistResult[]>;
  settlePendingMerge(): Observable<void>;
  /**
   * A method rather than a signal so nothing is fetched until a consumer asks — this service is
   * root-scoped and most pages render nothing that needs resolution. Memoised, so consumers share one.
   */
  resolveAgents(): ShortlistAgents;
}

@Injectable({
  providedIn: 'root',
})
export class ShortlistService implements CompareAgent {
  private readonly userService = inject(PublicUserContextService);
  private readonly localShortlistStorage = inject(LocalShortlistStorageService);
  private readonly apiShortlistStorage = inject(ApiShortlistStorageService);
  private readonly injector = inject(Injector);
  private readonly mergePrompt = inject(ShortlistMergePrompt, { optional: true });

  private readonly agentCodes = signal<string[]>([]);
  private readonly loadedCodes = signal<string[]>([]);
  private readonly pendingLocalCodes = signal<string[]>([]);
  private readonly authenticated = signal(false);
  private readonly storage = computed(() => (this.authenticated() ? this.apiShortlistStorage : this.localShortlistStorage));

  private readonly agentAdded = new Subject<string>();

  public readonly agentAdded$ = this.agentAdded.asObservable();

  public readonly shortlist = this.agentCodes.asReadonly();
  public readonly loadedShortlist = this.loadedCodes.asReadonly();
  public readonly shortlistCount = computed(() => this.shortlist().length);
  public readonly hasPendingMerge = computed(() => this.pendingLocalCodes().length > 0);
  public readonly location = undefined;

  private resolvedAgents: ShortlistAgents | undefined;
  private mergeInFlight: Observable<void> | undefined;

  public constructor() {
    // Reload whenever auth flips storage backends. switchMap cancels an in-flight
    // load so a slow API response can't clobber a newer local one on rapid toggles.
    this.userService.isAuthenticated$
      .pipe(
        tap((isAuthenticated) => {
          this.authenticated.set(isAuthenticated);
          this.pendingLocalCodes.set([]);
        }),
        switchMap((isAuthenticated) => (isAuthenticated ? this.loadApiAbsorbingLocal() : this.localShortlistStorage.load())),
        takeUntilDestroyed(),
      )
      .subscribe((agentCodes) => this.setCodes(agentCodes));
  }

  public resolveAgents(): ShortlistAgents {
    // Built on first ask and kept, so a second consumer joins the resolution already in flight
    // rather than starting its own. runInInjectionContext because this is called from a consumer's
    // field initialiser, not from this service's own construction.
    this.resolvedAgents ??= runInInjectionContext(this.injector, () => createShortlistAgents(this.shortlist));

    return this.resolvedAgents;
  }

  public clear(): Observable<ShortlistResult[]> {
    return this.storage()
      .clear()
      .pipe(
        tap((results) => {
          // Codes the store failed to drop are still shortlisted, so they stay. Emptying both
          // signals regardless would hide them from the user until the next load contradicted us.
          const remaining = results.filter(({ success }) => !success).map(({ agentCode }) => agentCode);

          this.setCodes(remaining);
        }),
      );
  }

  public remove(agentCode: string): Observable<ShortlistResult> {
    return this.afterMerge(() => this.storage().remove(agentCode)).pipe(
      tap((result) => {
        if (result) {
          this.agentCodes.update((codes) => codes.filter((code) => code !== agentCode));
        }
      }),
    );
  }

  public toggleShortList(agentCode: string): Observable<ShortlistResult> {
    return this.agentCodes().includes(agentCode) ? this.remove(agentCode) : this.add(agentCode);
  }

  public addMany(agentCodes: string[]): Observable<ShortlistResult[]> {
    return this.afterMerge(() => this.storage().addMany(agentCodes)).pipe(
      tap((results) => {
        const succeeded = results.filter(({ success }) => success).map(({ agentCode }) => agentCode);
        this.append(succeeded);
      }),
    );
  }

  // The question is asked where comparing agents is the task at hand — the compare page, or an
  // add/remove anywhere — never at the sign-in itself, which lands on whatever page the user left.
  // Shared so a second caller joins the open dialog instead of raising another. Never errors, so no
  // caller has to guard: a failure leaves the codes pending and the question is asked again.
  public settlePendingMerge(): Observable<void> {
    const localCodes = this.pendingLocalCodes();
    const apiCodes = this.agentCodes();

    if (!localCodes.length || !this.authenticated()) {
      return of(undefined);
    }

    this.mergeInFlight ??= this.askMergeChoice(localCodes, apiCodes).pipe(
      switchMap((keep) =>
        keep
          ? this.reconcile(apiCodes, keep).pipe(
              tap((agentCodes) => {
                this.pendingLocalCodes.set([]);
                this.setCodes(agentCodes);
              }),
              map(() => undefined),
            )
          : of(undefined),
      ),
      catchAndLogError(of(undefined)),
      finalize(() => (this.mergeInFlight = undefined)),
      share(),
    );

    return this.mergeInFlight;
  }

  // A shortlist built before signing in would otherwise vanish behind the account's own list, so the
  // local codes are held as pending until `settlePendingMerge` asks the user how the two join.
  private loadApiAbsorbingLocal(): Observable<string[]> {
    return this.localShortlistStorage.load().pipe(
      switchMap((localCodes) =>
        this.apiShortlistStorage.load().pipe(
          switchMap((apiCodes) => {
            if (!localCodes.length) {
              return of(apiCodes);
            }

            if (!apiCodes.length) {
              return this.reconcile([], localCodes);
            }

            this.pendingLocalCodes.set(localCodes);

            return of(apiCodes);
          }),
        ),
      ),
    );
  }

  private askMergeChoice(localCodes: string[], apiCodes: string[]): Observable<string[] | undefined> {
    return this.mergePrompt ? this.mergePrompt.choose(localCodes, apiCodes) : of([...new Set([...apiCodes, ...localCodes])]);
  }

  private afterMerge<T>(work: () => Observable<T>): Observable<T> {
    return this.settlePendingMerge().pipe(switchMap(work));
  }

  private setCodes(agentCodes: string[]): void {
    this.agentCodes.set(agentCodes);
    this.loadedCodes.set(agentCodes);
  }

  // Brings the account into line with `keep`: account codes the user dropped are deleted, kept codes
  // it lacks are added. There is no bulk delete, so drops go out in parallel. Local is cleared whether
  // or not its codes were kept — the user has answered for every one of them — but only once the API
  // has taken the additions, so a failed hand-over keeps them for the next sign-in. The final load
  // reads the account back rather than trusting the results, because `remove` reports a failed
  // delete as a success.
  private reconcile(apiCodes: string[], keep: string[]): Observable<string[]> {
    const dropped = apiCodes.filter((code) => !keep.includes(code));
    const added = keep.filter((code) => !apiCodes.includes(code));
    const dropped$ = dropped.length ? forkJoin(dropped.map((code) => this.apiShortlistStorage.remove(code))) : of([]);

    return dropped$.pipe(
      switchMap(() => (added.length ? this.apiShortlistStorage.addMany(added) : of<ShortlistResult[]>([]))),
      switchMap((results) => (results.every(({ success }) => success) ? this.localShortlistStorage.clear() : of([]))),
      switchMap(() => this.apiShortlistStorage.load()),
    );
  }

  private add(agentCode: string): Observable<ShortlistResult> {
    return this.afterMerge(() => this.storage().add(agentCode)).pipe(
      tap((result) => {
        if (result.success) {
          this.append([agentCode]);
          this.agentAdded.next(agentCode);
        }
      }),
    );
  }

  private append(agentCodes: string[]): void {
    this.agentCodes.update((codes) => {
      const additions = agentCodes.filter((code) => !codes.includes(code));
      return additions.length ? [...codes, ...additions] : codes;
    });
  }
}
